import { Button, Container, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios, { CancelToken } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEvent, INewEvent } from '../../../models/IEvent';
import EventServiceInstance from '../../../services/EventService';
import LoadingControl from '../../common/LoadingControl';
import SeasonSelectorControl from '../../common/SeasonSelectorControl';
import OrganizerEventsTable from './OrganizerEventsTable';
import AddRoadIcon from '@mui/icons-material/AddRoad';
import RefreshIcon from '@mui/icons-material/Refresh';
import NewEventDialog from './NewEventDialog';
import { useNavigate } from 'react-router-dom';
import OntimeBreadcrumb from '../../common/OntimeBreadcrumb';

function statusSortPriority(status: number): number {
    switch (status) {
        case 1: return 0; // live
        case 0: return 1; // upcoming
        case 2: return 2; // finished
        default: return 3;
    }
}

function sortOrganizerEvents(events: IEvent[]): IEvent[] {
    return [...events].sort((a, b) => {
        const priorityDiff = statusSortPriority(a.status) - statusSortPriority(b.status);
        if (priorityDiff !== 0) return priorityDiff;
        const timeA = a.eventDate ? new Date(a.eventDate).getTime() : NaN;
        const timeB = b.eventDate ? new Date(b.eventDate).getTime() : NaN;
        if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
        if (Number.isNaN(timeA)) return 1;
        if (Number.isNaN(timeB)) return -1;
        // Upcoming events (status 0) sorted ascending; live (1) and past (2) sorted descending
        if (a.status === 0) return timeA - timeB;
        return timeB - timeA;
    });
}

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    fixedHeight: {
        height: 240,
    },
}));

export default function OrganizerEvents() {
    const [organizerEvents, setEvents] = useState<IEvent[]>([]);
    const [newEvent, setNewEvent] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [selectedSeason, setSelectedSeason] = useState<number>(new Date().getFullYear());
    const { t } = useTranslation("common");
    const classes = useStyles();
    const navigate = useNavigate();

    const fetchEvents = useCallback(async (cancelToken?: CancelToken) => {
        setLoaded(false);
        try {
            const from = new Date(selectedSeason, 0, 1);
            const to = new Date(selectedSeason, 11, 31, 23, 59, 59, 999);
            const evts = await EventServiceInstance.getOrganizerEvents(from, to, cancelToken);
            setEvents(sortOrganizerEvents(evts));
            setLoaded(true);
        } catch (e) {
            if (!axios.isCancel(e)) {
                console.error(e);
                setEvents([]);
                setLoaded(true);
            }
        }
    }, [selectedSeason]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        fetchEvents(source.token);
        return () => {
            source.cancel();
        };
    }, [fetchEvents]);

    // events are fetched per-season from the server; no local filtering required

    const createEvent = () => {
        setNewEvent(true);
    }

    const handleCompleteEdit = async (evt: INewEvent) => {
        const savedEvent = await EventServiceInstance.newEvent(evt);
        navigate(`/event/setup/${savedEvent?.raceId}`);
    }

    return (
        <Container maxWidth="lg" className={classes.container} >
            <Grid container spacing={3} className={classes.container}>
                {/* Breadcrumb */}
                <Grid item xs={12}>
                    <OntimeBreadcrumb nameMap={{}} current={t('events.organizerEvents')} />
                </Grid>
                {/* Live events */}
                <Grid item xs={12}>
                    <Typography variant="h4"  >{t("events.organizerEvents")}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <NewEventDialog
                        open={newEvent}
                        onCancelEdit={() => setNewEvent(false)}
                        onCompleteEdit={handleCompleteEdit} />
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={e => createEvent()}
                        startIcon={<AddRoadIcon />}
                    >{t("eventsetup.new")}</Button>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Tooltip title={t("commonLabels.refresh")}>
                            <IconButton
                                onClick={() => fetchEvents()}
                                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                aria-label={t("commonLabels.refresh") as string}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            onClick={() => fetchEvents()}
                            startIcon={<RefreshIcon />}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                        >{t("commonLabels.refresh")}</Button>
                        <SeasonSelectorControl value={selectedSeason} onChange={setSelectedSeason} />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <OrganizerEventsTable events={organizerEvents} />
                        {loaded === true ? (<></>) : (<LoadingControl message={t("events.loading")} />)}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}